// saas-monetization-plans v2 — WU-L: PlansPage.
//
// Super Admin plan catalog: list/create/edit plans, deactivate (not delete).
// Mirrors MembershipPlansPage in structure (list + Dialog-wrapped form).

import {
  ApiError,
  activatePlan,
  createPlan,
  deactivatePlan,
  listPlans,
  updatePlan,
} from '@jq4race/api-client';
import { LocalizedError, Trans, useTranslation } from '@jq4race/i18n';
import type { CreatePlanRequest, PlanResponse } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatCents,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import {
  CheckCircle2,
  CreditCard,
  Pencil,
  Plus,
  Power,
  PowerOff,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { PlanForm } from '../components/PlanForm';

export function PlansPage() {
  const { t } = useTranslation('plans');
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // plans", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<PlanResponse | 'new' | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PlanResponse | null>(null);
  const [activateTarget, setActivateTarget] = useState<PlanResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listPlans();
      setPlans(res.plans);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('page.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleFormSubmit(values: CreatePlanRequest) {
    try {
      if (editTarget && editTarget !== 'new') {
        await updatePlan(editTarget.id, values);
        setSuccess(t('page.updated'));
      } else {
        await createPlan(values);
        setSuccess(t('page.created'));
      }
      setEditTarget(null);
      void reload();
    } catch (e) {
      if (e instanceof ApiError)
        throw new LocalizedError(translateApiError(e, t, { fallback: t('page.saveError') }));
      throw e;
    }
  }

  async function handleDeactivateConfirm() {
    if (!deactivateTarget) return;
    try {
      await deactivatePlan(deactivateTarget.id);
      setSuccess(t('page.deactivated', { name: deactivateTarget.name }));
      setDeactivateTarget(null);
      void reload();
    } catch (e) {
      setDeactivateTarget(null);
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('page.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('page.operationFailed') }));
    }
  }

  async function handleActivateConfirm() {
    if (!activateTarget) return;
    try {
      await activatePlan(activateTarget.id);
      setSuccess(t('page.activated', { name: activateTarget.name }));
      setActivateTarget(null);
      void reload();
    } catch (e) {
      setActivateTarget(null);
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('page.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('page.operationFailed') }));
    }
  }

  const formOpen = editTarget !== null;
  const formMode = editTarget === 'new' ? 'create' : 'edit';
  const formDefaults =
    editTarget && editTarget !== 'new'
      ? {
          name: editTarget.name,
          description: editTarget.description ?? '',
          subscriptionAmountCents: editTarget.subscriptionAmountCents / 100,
          currency: editTarget.currency,
          interval: editTarget.interval,
          intervalCount: editTarget.intervalCount,
          includedVehicles: editTarget.includedVehicles,
          overagePriceCents: editTarget.overagePriceCents / 100,
          commissionEnabled: editTarget.commissionEnabled,
          commissionBasisPoints: editTarget.commissionBasisPoints,
          features: editTarget.features,
        }
      : undefined;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && plans.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{plans.length}</span>{' '}
              {t('page.count', { count: plans.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Button size="sm" onClick={() => setEditTarget('new')}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('page.newPlan')}
          </Button>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <PlansTable
              plans={plans}
              loading={loading}
              onEdit={setEditTarget}
              onDeactivate={setDeactivateTarget}
              onActivate={setActivateTarget}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(next) => (!next ? setEditTarget(null) : undefined)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? t('page.newPlanDialogTitle') : t('page.editPlanDialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-6">
            <PlanForm
              mode={formMode}
              defaultValues={formDefaults}
              onSubmit={handleFormSubmit}
              onCancel={() => setEditTarget(null)}
              submitLabel={formMode === 'create' ? t('page.createPlan') : t('page.saveChanges')}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deactivateTarget !== null}
        onOpenChange={(next) => (!next ? setDeactivateTarget(null) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deactivateDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="deactivateDialog.description"
                values={{ name: deactivateTarget?.name }}
                components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeactivateTarget(null)}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => void handleDeactivateConfirm()}
            >
              {t('deactivateDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={activateTarget !== null}
        onOpenChange={(next) => (!next ? setActivateTarget(null) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('activateDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="activateDialog.description"
                values={{ name: activateTarget?.name }}
                components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setActivateTarget(null)}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button type="button" size="sm" onClick={() => void handleActivateConfirm()}>
              {t('activateDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────

interface PlansTableProps {
  plans: PlanResponse[];
  loading: boolean;
  onEdit: (plan: PlanResponse) => void;
  onDeactivate: (plan: PlanResponse) => void;
  onActivate: (plan: PlanResponse) => void;
}

function PlansTable({ plans, loading, onEdit, onDeactivate, onActivate }: PlansTableProps) {
  const { t } = useTranslation('plans');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.name')}</TableHead>
          <TableHead>{t('table.price')}</TableHead>
          <TableHead>{t('table.includedVehicles')}</TableHead>
          <TableHead>{t('table.overage')}</TableHead>
          <TableHead>{t('table.commission')}</TableHead>
          <TableHead>{t('table.status')}</TableHead>
          <TableHead className="w-[100px] text-right">{t('table.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && plans.length === 0 ? (
          <SkeletonRows />
        ) : plans.length === 0 ? (
          <TableEmpty colSpan={7}>
            <EmptyState />
          </TableEmpty>
        ) : (
          plans.map((plan) => (
            <TableRow key={plan.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{plan.name}</span>
              </TableCell>
              <TableCell className="text-jq-graphite">
                {t('table.priceInterval', {
                  amount: formatCents(plan.subscriptionAmountCents, plan.currency),
                  interval: t(`table.intervalLabel.${plan.interval}`),
                })}
              </TableCell>
              <TableCell className="text-jq-graphite tabular-nums">
                {plan.includedVehicles}
              </TableCell>
              <TableCell className="text-jq-graphite">
                {t('table.overagePerVehicle', {
                  amount: formatCents(plan.overagePriceCents, plan.currency),
                })}
              </TableCell>
              <TableCell>
                {plan.commissionEnabled ? (
                  <Badge variant="accent">{formatBps(plan.commissionBasisPoints)}</Badge>
                ) : (
                  <span className="text-[13px] text-jq-graphite/40">
                    {t('table.commissionOff')}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={plan.active ? 'success' : 'secondary'}>
                  {plan.active ? t('table.active') : t('table.inactive')}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onEdit(plan)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                    aria-label={t('table.edit', { name: plan.name })}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                  {plan.active ? (
                    <button
                      type="button"
                      onClick={() => onDeactivate(plan)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                      aria-label={t('table.deactivate', { name: plan.name })}
                    >
                      <PowerOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onActivate(plan)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                      aria-label={t('table.activate', { name: plan.name })}
                    >
                      <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 7 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('plans');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <CreditCard className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('empty.description')}</p>
    </div>
  );
}

/** Format bps as a human-readable percentage string, e.g. 500 → "5.00%" */
function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('plans');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
